import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Stars({ value, onChange, interactive = false, size = 16 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => interactive && onChange && onChange(n)}
          disabled={!interactive}
          aria-label={`${n} star`}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          data-testid={interactive ? `star-input-${n}` : undefined}
        >
          <Star
            size={size}
            className={`${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-600"}`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <div className="forge-card p-6 w-[300px] md:w-[360px] shrink-0">
      <Stars value={r.rating} />
      <h4 className="font-display text-lg tracking-tight text-slate-100 mt-3">{r.title}</h4>
      <p className="font-body text-slate-400 text-sm mt-2 leading-relaxed line-clamp-4">
        {r.content}
      </p>
      <div className="mt-4 pt-4 border-t border-white/5">
        <div className="font-tech text-[11px] text-orange-400">{r.name}</div>
        {r.company && (
          <div className="font-tech text-[10px] text-slate-500 mt-0.5">{r.company}</div>
        )}
      </div>
    </div>
  );
}

const FALLBACK_REVIEWS = [
  {
    id: "f1",
    name: "Daniel Reeves",
    company: "Reeves Roofing Co.",
    title: "Real ROI from day one",
    content: "Master Ready Services delivered storm leads we could actually close. Within the first three weeks, our team booked over 40 inspections and signed 11 jobs. The quality difference vs. typical lead vendors is night and day.",
    rating: 5,
    status: "approved",
  },
  {
    id: "f2",
    name: "Carla Mendez",
    company: "Sunline Solar",
    title: "Pipeline finally predictable",
    content: "We tried three other lead-gen companies before finding MRS. They actually built a solar campaign around our service area and qualification rules. Conversions doubled.",
    rating: 5,
    status: "approved",
  },
  {
    id: "f3",
    name: "Robert Hayes",
    company: "Hayes Restoration",
    title: "Rock Solid is the real deal",
    content: "Rock Solid Storm Leads identified a hail market we missed by two days. The leads were warm, qualified, and ready to talk insurance. Easily our best-performing source this season.",
    rating: 5,
    status: "approved",
  },
];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({
    name: "",
    company: "",
    rating: 5,
    title: "",
    content: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(Array.isArray(data) && data.length > 0 ? data : FALLBACK_REVIEWS);
    } catch {
      setReviews(FALLBACK_REVIEWS);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.title.trim() || form.content.trim().length < 5) {
      toast.error("Please fill in name, title and a review (at least 5 characters)");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Review submitted! It will appear after approval.");
      setForm({ name: "", company: "", rating: 5, title: "", content: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const trackList = [...reviews, ...reviews];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-[#0B1120]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-amber-500" />
              <span className="font-tech text-amber-400 text-xs">CONTRACTOR FEEDBACK</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              <span className="chrome-text">What Our</span>
              <br />
              <span className="ember-text">Clients Say.</span>
            </h2>
            <p className="mt-6 font-body text-slate-400 max-w-xl">
              Real feedback from contractors, roofers, solar pros, and restoration teams
              running our pipelines.
            </p>
          </div>

          <div className="lg:col-span-5">
            <form
              onSubmit={onSubmit}
              data-testid="review-form"
              className="forge-card p-6"
            >
              <div className="font-display text-xl tracking-tight chrome-text mb-1">
                Leave a Review
              </div>
              <div className="font-tech text-[10px] text-slate-500 mb-5">
                Posted publicly after approval
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="review-input-name"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="col-span-1 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
                />
                <input
                  data-testid="review-input-company"
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="col-span-1 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
                />
              </div>

              <input
                data-testid="review-input-title"
                placeholder="Review Title *"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full mt-3 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
              />

              <textarea
                data-testid="review-input-content"
                placeholder="Tell us about working with us... *"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={4}
                className="w-full mt-3 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60 resize-none"
              />

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-3">
                  <span className="font-tech text-[10px] text-slate-400">RATING</span>
                  <Stars
                    value={form.rating}
                    onChange={(v) => setForm({ ...form, rating: v })}
                    interactive
                    size={18}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="review-submit-btn"
                  disabled={submitting}
                  className="btn-forge text-[11px] py-2 px-4 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Submit"} <Send size={12} />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Marquee */}
        <div className="marquee-container overflow-hidden relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[#0B1120] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[#0B1120] to-transparent z-10" />
          <div className="marquee-track py-4">
            {trackList.map((r, i) => (
              <ReviewCard key={`${r.id}-${i}`} r={r} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
